import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

/**
 * Inactivity timeout for the SPA (spec §3.7).
 *
 * While authenticated, any user activity (click / keydown / mousemove / scroll /
 * touch) resets a 15-minute idle timer. On expiry the session is logged out and
 * the user is sent to the login screen with `reason=timeout`. Mirrors the
 * server-side SESSION_LIFETIME=15 so the FE never sits on a silently-dead session.
 *
 * Instantiated at bootstrap (see app.config.ts). It reacts to `AuthService`
 * state via an effect — starting the timer on login, stopping it on logout — so
 * there's no circular start/stop wiring in AuthService itself.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly doc = inject(DOCUMENT);

  /** Idle window — keep in sync with the API's SESSION_LIFETIME (minutes). */
  private readonly timeoutMs = 15 * 60 * 1000;
  private readonly activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'] as const;
  private readonly onActivity = (): void => this.resetTimer();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private tracking = false;

  constructor() {
    effect(() => {
      if (this.auth.isAuthenticated()) {
        this.start();
      } else {
        this.stop();
      }
    });
  }

  private start(): void {
    if (this.tracking) {
      return;
    }
    this.tracking = true;
    // Listen outside Angular so mousemove doesn't trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.activityEvents.forEach((e) =>
        this.doc.addEventListener(e, this.onActivity, { passive: true })
      );
    });
    this.resetTimer();
  }

  private stop(): void {
    if (!this.tracking) {
      return;
    }
    this.tracking = false;
    this.activityEvents.forEach((e) => this.doc.removeEventListener(e, this.onActivity));
    this.clear();
  }

  private resetTimer(): void {
    this.clear();
    this.zone.runOutsideAngular(() => {
      this.timer = setTimeout(() => this.expire(), this.timeoutMs);
    });
  }

  private clear(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private expire(): void {
    this.stop();
    // Back inside Angular for logout + navigation.
    this.zone.run(() => {
      const goToLogin = () =>
        this.router.navigate(['/authentication'], { queryParams: { reason: 'timeout' } });
      this.auth.logout().subscribe({ next: goToLogin, error: goToLogin });
    });
  }
}
